/**
 * upcart-bundle-property-cleanup.js
 *
 * Two jobs, both targeted at the bundle "Includes" property pattern only:
 *
 *   1. Strip the trailing ":" UpCart bakes into property keys (CSS can't
 *      reach text content; the colon lives inside a text node in UpCart's
 *      shadow DOM at .upcart-public-cart-items__key-value-pair).
 *
 *   2. Split bullet rows on the em-dash separator " — " into a flavor half
 *      and a type half, and wrap the type half in a <span class="ts-bundle
 *      -type"> so it can be styled smaller + dimmer than the flavor name.
 *      A single <style> element is injected into UpCart's shadow root with
 *      the styling rules.
 *
 * Targeted exclusively at:
 *   • bullet-prefixed keys (^•\s …) — the bundle component rows
 *   • the literal "Includes" header row
 *
 * Em-dash separator was chosen because no Teeccino product title contains
 * one — splitting on regular hyphen would mangle accessory titles like
 * "Frother Friend - Electric Milk Frother".
 *
 * Idempotent: each pass checks if the span has already been processed
 * (children present, or text already without a trailing colon) and bails.
 * Safe under the MutationObserver loop.
 *
 * Loaded globally from layout/theme.liquid so the cleanup applies
 * regardless of which page the cart drawer is opened from.
 */
(function () {
  'use strict';

  var BULLET_PREFIX = /^•\s/;
  var INCLUDES_HEADER = /^Includes:?\s*$/;
  var TRAILING_COLON = /:\s*$/;
  var EM_DASH_SEP = ' — ';

  var STYLE_ID = 'themesmith-bundle-property-style';
  var STYLE_CSS = [
    '.ts-bundle-type {',
    '  font-size: 0.82em;',
    '  opacity: 0.65;',
    '  font-weight: 400;',
    '}',
    '.ts-bundle-free {',
    '  font-size: 0.78em;',
    '  font-weight: 700;',
    '  letter-spacing: 0.06em;',
    '  color: #4a7c4f;',
    '  opacity: 1;',
    '}'
  ].join('\n');

  function ensureStyleInjected(shadowRoot) {
    if (shadowRoot.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE_CSS;
    shadowRoot.appendChild(style);
  }

  function processBundleSpan(span) {
    // Idempotency: if we already split this span into child elements, skip.
    if (span.children && span.children.length > 0) return;

    var text = span.textContent;

    var isBulletRow = BULLET_PREFIX.test(text);
    var isIncludesHeader = INCLUDES_HEADER.test(text);
    if (!isBulletRow && !isIncludesHeader) return;

    // Strip trailing colon UpCart adds to every key.
    var stripped = text.replace(TRAILING_COLON, '');

    // The Includes header doesn't get split; we just write back the colon
    // -free text. textContent assignment is a no-op when the value already
    // matches, so subsequent MutationObserver passes don't re-fire.
    if (isIncludesHeader) {
      if (span.textContent !== stripped) span.textContent = stripped;
      return;
    }

    // Bullet row. Try to split on em-dash separator.
    var sepIdx = stripped.indexOf(EM_DASH_SEP);
    if (sepIdx === -1) {
      // Bullet row without a type suffix (e.g. accessory items where the
      // type was dropped on the Liquid side). Just colon-strip.
      if (span.textContent !== stripped) span.textContent = stripped;
      return;
    }

    // Split into flavor + type, build child spans.
    var flavor = stripped.substring(0, sepIdx);
    var typeText = stripped.substring(sepIdx); // includes the leading ' — '

    span.textContent = '';
    var flavorEl = document.createElement('span');
    flavorEl.textContent = flavor;
    var typeEl = document.createElement('span');
    // Detect "FREE" callouts (accessory rows) — apply the bold sage-green
    // accent class instead of the dim grey type-suffix class. The em-dash
    // is part of typeText; we test against the trimmed remainder.
    if (/\bFREE\s*$/.test(typeText)) {
      typeEl.className = 'ts-bundle-free';
    } else {
      typeEl.className = 'ts-bundle-type';
    }
    typeEl.textContent = typeText;
    span.appendChild(flavorEl);
    span.appendChild(typeEl);
  }

  function processAllBundleSpans() {
    var root = document.getElementById('upCart');
    if (!root || !root.shadowRoot) return;
    var sr = root.shadowRoot;
    ensureStyleInjected(sr);
    var spans = sr.querySelectorAll(
      '.upcart-public-product-properties__item span.upcart-public-cart-items__key-value-pair'
    );
    for (var i = 0; i < spans.length; i++) processBundleSpan(spans[i]);
  }

  function wireUpCartEvents() {
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(processAllBundleSpans);
      window.upcartSubscribeCartUpdated(processAllBundleSpans);
      window.upcartSubscribeCartOpened(processAllBundleSpans);
      // First-paint pass — preloaded cart may already be in the DOM
      processAllBundleSpans();
      return true;
    }
    return false;
  }

  // UpCart's API attaches asynchronously. Poll briefly until ready.
  if (!wireUpCartEvents()) {
    var attempts = 0;
    var poll = setInterval(function () {
      attempts += 1;
      if (wireUpCartEvents() || attempts >= 50) clearInterval(poll); // ~10s ceiling
    }, 200);
  }

  // Belt-and-suspenders: a MutationObserver on the shadow root catches any
  // late re-renders that aren't routed through the Subscribe callbacks
  // (e.g. quantity changes, upsell-add). The processor is a no-op on
  // already-handled spans, so the observer loop is cheap.
  function attachObserver() {
    var root = document.getElementById('upCart');
    if (!root || !root.shadowRoot) {
      setTimeout(attachObserver, 500);
      return;
    }
    var obs = new MutationObserver(function () {
      // Microtask schedule to avoid re-entrancy in the same tick the
      // mutation fired.
      Promise.resolve().then(processAllBundleSpans);
    });
    obs.observe(root.shadowRoot, { subtree: true, childList: true, characterData: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObserver);
  } else {
    attachObserver();
  }
})();
